import type { IMessage, IChatOptions, IMessageReferenceData } from "../models/index.js";

export type RemoteRequestDirection = "older" | "newer" | "latest";
export type RemoteRequestReason = "edge-scroll" | "scroll-to-bottom" | "reference-jump";
export type RemoteRequestMode = "replace" | "prepend" | "append";
export type RemoteLoadingState = "older" | "newer" | "all";

export interface IRemoteRangeRequestOptions {
    startIndex: number;
    endIndex: number;
    direction: RemoteRequestDirection;
    reason: RemoteRequestReason;
    mode: RemoteRequestMode;
    loadingState: RemoteLoadingState;
    scrollToBottom?: boolean;
    targetMessageId?: string | number;
}

interface IRemoteRangeRequestState {
    key: string;
    options: IRemoteRangeRequestOptions;
}

interface IPreparedRemoteRangeRequest {
    key: string;
    options: IRemoteRangeRequestOptions;
}

export interface IRemoteParsedResponse {
    range: { startIndex: number; endIndex: number };
    models: IMessage[];
    totalCount: number;
}

interface IRemoteRequestLifecycle {
    finalize: () => void;
    fail: (error: unknown) => void;
}

export interface IRemoteRangeLoaderContext {
    getDataSource: () => any;
    getChatOptions: () => IChatOptions;
    getTotalCount: () => number;
    getLoadedRange: () => { startIndex: number; endIndex: number };
    getRenderedRange: () => { startIndex: number; endIndex: number };
    getLoadedMessagesInRange: (startIndex: number, endIndex: number) => IMessage[];
    getLoadedMessageById: (id: string | number) => IMessage | null;
    getReferenceMessageById: (id: string | number) => IMessageReferenceData | null;
    cacheResolvedReferenceMessages: (messages: IMessageReferenceData[]) => void;
    applyResponse: (requestOptions: IRemoteRangeRequestOptions, parsedResponse: IRemoteParsedResponse) => void;
    updateEndlessState: () => void;
    completeEndlessLoad: (direction: RemoteLoadingState, callback?: () => void) => void;
    setRemoteLoadingState: (direction: RemoteLoadingState) => void;
    canRequestRange: () => boolean;
}

export class RemoteRangeLoader {
    private context: IRemoteRangeLoaderContext;
    private _pendingRemoteRangeRequests: Record<string, IRemoteRangeRequestState> = {};
    private _activeLatestRecoveryRequestKey: string | null = null;
    private _queuedLatestRecoveryRequest: IRemoteRangeRequestOptions | null = null;

    constructor(context: IRemoteRangeLoaderContext) {
        this.context = context;
    }

    reset(): void {
        this._pendingRemoteRangeRequests = {};
        this._activeLatestRecoveryRequestKey = null;
        this._queuedLatestRecoveryRequest = null;
    }

    requestRange(options: IRemoteRangeRequestOptions): void {
        const preparedRequest = this._prepareRequest(options);

        if (!preparedRequest) {
            this.context.updateEndlessState();
            return;
        }

        if (this._mergePendingRequest(preparedRequest) || this._queueLatestRecoveryRequestIfNeeded(preparedRequest)) {
            return;
        }

        const requestState = this._activateRequest(preparedRequest);

        this._dispatchRequest(requestState);
    }

    private _prepareRequest(options: IRemoteRangeRequestOptions): IPreparedRemoteRangeRequest | null {
        const requestOptions = this._normalizeRequestOptions(options);

        if (!requestOptions) {
            return null;
        }

        return {
            key: this._getRemoteRangeRequestKey(requestOptions),
            options: requestOptions
        };
    }

    private _normalizeRequestOptions(options: IRemoteRangeRequestOptions): IRemoteRangeRequestOptions | null {
        const normalizedRange = this._normalizeRequestedRange(options);

        if (!normalizedRange) {
            return null;
        }

        return {
            ...options,
            startIndex: normalizedRange.startIndex,
            endIndex: normalizedRange.endIndex
        };
    }

    private _normalizeRequestedRange(options: IRemoteRangeRequestOptions): { startIndex: number; endIndex: number } | null {
        const totalCount = this.context.getTotalCount();
        const isLatestScrollToBottomRequest = this._isLatestScrollToBottomRequest(options);
        let safeStartIndex = Math.max(0, options.startIndex);
        let safeEndIndex = Math.max(safeStartIndex, options.endIndex);

        if (isLatestScrollToBottomRequest) {
            const requestedWindow = Math.max(1, safeEndIndex - safeStartIndex);

            return {
                startIndex: safeStartIndex,
                endIndex: safeStartIndex + requestedWindow
            };
        }

        safeStartIndex = Math.max(0, Math.min(safeStartIndex, totalCount));
        safeEndIndex = Math.max(safeStartIndex, Math.min(safeEndIndex, totalCount));

        if (safeEndIndex <= safeStartIndex) {
            return null;
        }

        return {
            startIndex: safeStartIndex,
            endIndex: safeEndIndex
        };
    }

    private _mergePendingRequest(preparedRequest: IPreparedRemoteRangeRequest): boolean {
        const pendingRequest = this._pendingRemoteRangeRequests[preparedRequest.key];

        if (!pendingRequest) {
            return false;
        }

        pendingRequest.options.scrollToBottom = pendingRequest.options.scrollToBottom || !!preparedRequest.options.scrollToBottom;
        pendingRequest.options.loadingState = this._mergeRemoteLoadingState(pendingRequest.options.loadingState, preparedRequest.options.loadingState);
        this.context.setRemoteLoadingState(pendingRequest.options.loadingState);

        return true;
    }

    private _queueLatestRecoveryRequestIfNeeded(preparedRequest: IPreparedRemoteRangeRequest): boolean {
        if (!this._isLatestScrollToBottomRequest(preparedRequest.options)
            || !this._activeLatestRecoveryRequestKey
            || this._activeLatestRecoveryRequestKey === preparedRequest.key) {
            return false;
        }

        this._queueLatestRecoveryRequest(preparedRequest.options);
        this.context.setRemoteLoadingState(this._queuedLatestRecoveryRequest!.loadingState);

        return true;
    }

    private _activateRequest(preparedRequest: IPreparedRemoteRangeRequest): IRemoteRangeRequestState {
        const requestState: IRemoteRangeRequestState = {
            key: preparedRequest.key,
            options: preparedRequest.options
        };

        this._pendingRemoteRangeRequests[requestState.key] = requestState;

        if (this._isLatestScrollToBottomRequest(requestState.options)) {
            this._activeLatestRecoveryRequestKey = requestState.key;
        }

        this.context.setRemoteLoadingState(requestState.options.loadingState);

        return requestState;
    }

    private _dispatchRequest(requestState: IRemoteRangeRequestState): void {
        const dataSource = this.context.getDataSource();
        const transportRead = dataSource?.transport?.read;

        if (typeof transportRead !== "function") {
            this._handleRequestError(requestState);
            return;
        }

        transportRead({
            data: this._createTransportRequestData(requestState.options),
            success: (response: any) => {
                this._handleRequestSuccess(requestState, response);
            },
            error: () => {
                this._handleRequestError(requestState);
            }
        });
    }

    private _createTransportRequestData(options: IRemoteRangeRequestOptions): any {
        return this._buildRemoteRequestData(
            options.startIndex,
            options.endIndex,
            options.direction,
            options.reason,
            options.targetMessageId
        );
    }

    private _handleRequestSuccess(requestState: IRemoteRangeRequestState, response: any): void {
        const dataSource = this.context.getDataSource();
        const lifecycle = this._createRequestLifecycle(requestState);

        if (!dataSource) {
            lifecycle.finalize();
            return;
        }

        try {
            const parsedResponse = this._parseResponse(dataSource, response, requestState.options);

            this._resolveMissingReplyReferences(requestState.options, parsedResponse.models, () => {
                this._applyResponse(requestState.options, parsedResponse);
                lifecycle.finalize();
            }, lifecycle.fail);
        } catch (error) {
            if (this._pendingRemoteRangeRequests[requestState.key]) {
                lifecycle.finalize();
            }

            throw error;
        }
    }

    private _createRequestLifecycle(requestState: IRemoteRangeRequestState): IRemoteRequestLifecycle {
        return {
            finalize: () => {
                this._completeRequest(requestState);
            },
            fail: (error: unknown) => {
                this._completeRequest(requestState);
                throw this._normalizeReferenceResolverError(error);
            }
        };
    }

    private _parseResponse(
        dataSource: any,
        response: any,
        requestOptions: IRemoteRangeRequestOptions
    ): IRemoteParsedResponse {
        const parsedResponse = dataSource.reader.parse(response);
        const items = dataSource.reader.data(parsedResponse) || [];
        const responseTotal = Number(dataSource.reader.total(parsedResponse));
        const totalCount = this._extractRemoteResponseTotal(responseTotal);
        const models = this._createRemoteModels(items);
        const range = this._extractRemoteResponseRange(parsedResponse, models.length, totalCount, requestOptions);

        this._validateReferenceJumpResponse(requestOptions, models);
        this._validateAdjacentRangeResponse(requestOptions, range, models);

        return {
            range,
            models,
            totalCount
        };
    }

    private _applyResponse(requestOptions: IRemoteRangeRequestOptions, parsedResponse: IRemoteParsedResponse): void {
        this.context.applyResponse(requestOptions, parsedResponse);
    }

    private _handleRequestError(requestState: IRemoteRangeRequestState): void {
        this.context.updateEndlessState();
        this._completeRequest(requestState);
    }

    private _completeRequest(requestState: IRemoteRangeRequestState): void {
        const queuedLatestRequest = this._clearRequestState(requestState.key);

        this.context.completeEndlessLoad(requestState.options.loadingState, () => {
            this._resumeQueuedLatestRecoveryRequest(queuedLatestRequest);
        });
    }

    private _clearRequestState(requestKey: string): IRemoteRangeRequestOptions | null {
        const queuedLatestRequest = this._consumeQueuedLatestRecoveryRequest(requestKey);

        delete this._pendingRemoteRangeRequests[requestKey];

        if (this._activeLatestRecoveryRequestKey === requestKey) {
            this._activeLatestRecoveryRequestKey = null;
        }

        return queuedLatestRequest;
    }

    private _resumeQueuedLatestRecoveryRequest(requestOptions: IRemoteRangeRequestOptions | null): void {
        if (requestOptions && this.context.canRequestRange()) {
            this.requestRange(requestOptions);
        }
    }

    private _mergeRemoteLoadingState(current: RemoteLoadingState, next: RemoteLoadingState): RemoteLoadingState {
        if (current === "all" || next === "all" || current !== next) {
            return "all";
        }

        return current;
    }

    private _getRemoteRangeRequestKey(options: IRemoteRangeRequestOptions): string {
        return [
            options.startIndex,
            options.endIndex,
            options.direction,
            options.reason,
            options.mode,
            options.targetMessageId == null ? "" : String(options.targetMessageId)
        ].join("|");
    }

    private _isLatestScrollToBottomRequest(options: IRemoteRangeRequestOptions): boolean {
        return options.direction === "latest" && options.reason === "scroll-to-bottom" && options.mode === "replace";
    }

    private _isReferenceJumpRequest(options: IRemoteRangeRequestOptions): boolean {
        return options.reason === "reference-jump" && options.mode === "replace" && options.targetMessageId != null;
    }

    private _isLatestRecoveryRequest(options: IRemoteRangeRequestOptions): boolean {
        if (!this._isLatestScrollToBottomRequest(options)) {
            return false;
        }

        const renderedRange = this.context.getRenderedRange();

        return renderedRange.endIndex > renderedRange.startIndex;
    }

    private _queueLatestRecoveryRequest(options: IRemoteRangeRequestOptions): void {
        if (!this._queuedLatestRecoveryRequest) {
            this._queuedLatestRecoveryRequest = { ...options };
            return;
        }

        this._queuedLatestRecoveryRequest = {
            ...options,
            scrollToBottom: this._queuedLatestRecoveryRequest.scrollToBottom || !!options.scrollToBottom,
            loadingState: this._mergeRemoteLoadingState(this._queuedLatestRecoveryRequest.loadingState, options.loadingState)
        };
    }

    private _consumeQueuedLatestRecoveryRequest(requestKey: string): IRemoteRangeRequestOptions | null {
        if (this._activeLatestRecoveryRequestKey !== requestKey || !this._queuedLatestRecoveryRequest) {
            return null;
        }

        const queuedRequest = this._queuedLatestRecoveryRequest;

        this._queuedLatestRecoveryRequest = null;

        return queuedRequest;
    }

    private _buildRemoteRequestData(
        startIndex: number,
        endIndex: number,
        direction: RemoteRequestDirection,
        reason: RemoteRequestReason,
        targetMessageId?: string | number
    ): any {
        const totalCount = this.context.getTotalCount();
        const safeStartIndex = Math.max(0, startIndex);
        const safeEndIndex = Math.max(safeStartIndex, Math.min(endIndex, totalCount || endIndex));
        const take = Math.max(1, safeEndIndex - safeStartIndex);
        const page = totalCount ? Math.max(1, Math.ceil(Math.max(totalCount - safeEndIndex, 0) / take) + 1) : 1;

        if (direction === "latest" && reason === "scroll-to-bottom") {
            return {
                page,
                pageSize: take,
                skip: safeStartIndex,
                take,
                direction,
                reason,
                intent: "latest",
                targetMessageId
            };
        }

        if (reason === "reference-jump") {
            return {
                page: 1,
                pageSize: take,
                skip: 0,
                take,
                direction,
                reason,
                targetMessageId
            };
        }

        return {
            page,
            pageSize: take,
            skip: safeStartIndex,
            take,
            startIndex: safeStartIndex,
            endIndex: safeEndIndex,
            direction,
            reason,
            targetMessageId
        };
    }

    private _extractRemoteResponseTotal(totalCount: number): number {
        if (!Number.isInteger(totalCount) || totalCount < 0) {
            throw new Error("The Chat endless response must include a valid total value.");
        }

        return totalCount;
    }

    private _extractRemoteResponseRange(
        parsedResponse: any,
        itemCount: number,
        totalCount: number,
        requestOptions: IRemoteRangeRequestOptions
    ): { startIndex: number; endIndex: number } {
        const responseStartIndex = Number(parsedResponse?.startIndex);
        const responseEndIndex = Number(parsedResponse?.endIndex);

        if (!Number.isInteger(responseStartIndex) || !Number.isInteger(responseEndIndex)) {
            throw new Error("The Chat endless response must include valid startIndex and endIndex values.");
        }

        const startIndex = responseStartIndex;
        const endIndex = responseEndIndex;

        if (startIndex < 0 || endIndex < startIndex || endIndex > totalCount) {
            throw new Error("The Chat endless response returned an invalid range window.");
        }

        if (endIndex - startIndex !== itemCount) {
            throw new Error("The Chat endless response count does not match the returned range.");
        }

        if (this._isLatestScrollToBottomRequest(requestOptions) && endIndex !== totalCount) {
            if (!this._isLatestRecoveryRequest(requestOptions)
                || startIndex !== requestOptions.startIndex
                || endIndex !== requestOptions.endIndex) {
                throw new Error("The Chat endless latest response must end at total.");
            }
        }

        return {
            startIndex,
            endIndex
        };
    }

    private _validateReferenceJumpResponse(
        requestOptions: IRemoteRangeRequestOptions,
        models: IMessage[]
    ): void {
        if (!this._isReferenceJumpRequest(requestOptions)) {
            return;
        }

        const targetMessageId = String(requestOptions.targetMessageId);
        const containsTarget = models.some((message: IMessage) => String(message?.id) === targetMessageId);

        if (!containsTarget) {
            throw new Error("The Chat endless jump response must contain the requested targetMessageId.");
        }
    }

    private _validateAdjacentRangeResponse(
        requestOptions: IRemoteRangeRequestOptions,
        range: { startIndex: number; endIndex: number },
        models: IMessage[]
    ): void {
        if (requestOptions.mode !== "prepend" && requestOptions.mode !== "append") {
            return;
        }

        if (range.startIndex !== requestOptions.startIndex || range.endIndex !== requestOptions.endIndex) {
            throw new Error("The Chat endless response must match the requested adjacent range.");
        }

        const loadedRange = this.context.getLoadedRange();

        if (requestOptions.mode === "prepend" && range.endIndex !== loadedRange.startIndex) {
            throw new Error("The Chat endless response must match the requested adjacent range.");
        }

        if (requestOptions.mode === "append" && range.startIndex !== loadedRange.endIndex) {
            throw new Error("The Chat endless response must match the requested adjacent range.");
        }

        const loadedMessages = this.context.getLoadedMessagesInRange(loadedRange.startIndex, loadedRange.endIndex);
        const loadedIds = new Set(loadedMessages.map((message: IMessage) => String(message.id)));

        if (models.some((message: IMessage) => loadedIds.has(String(message.id)))) {
            throw new Error("The Chat endless response must match the requested adjacent range.");
        }
    }

    private _createRemoteModels(items: any[]): IMessage[] {
        const dataSource = this.context.getDataSource();
        const model = dataSource?.reader?.model;

        return items.map((item: any) => {
            return model ? new model(item) : item;
        });
    }

    private _collectMissingReplyTargetIds(
        requestOptions: IRemoteRangeRequestOptions,
        models: IMessage[]
    ): Array<string | number> {
        const missingIds: Array<string | number> = [];
        const seenIds = new Set<string>();
        const availableIds = new Set<string>();

        if (requestOptions.mode !== "replace") {
            const loadedRange = this.context.getLoadedRange();
            const loadedMessages = this.context.getLoadedMessagesInRange(loadedRange.startIndex, loadedRange.endIndex);

            loadedMessages.forEach((message: IMessage) => {
                if (message?.id != null) {
                    availableIds.add(String(message.id));
                }
            });
        }

        models.forEach((message: IMessage) => {
            if (message?.id != null) {
                availableIds.add(String(message.id));
            }
        });

        models.forEach((message: IMessage) => {
            const replyToId = message?.replyToId;

            if (!replyToId) {
                return;
            }

            const key = String(replyToId);

            if (availableIds.has(key)
                || this.context.getReferenceMessageById(replyToId)
                || seenIds.has(key)) {
                return;
            }

            seenIds.add(key);
            missingIds.push(replyToId);
        });

        return missingIds;
    }

    private _resolveMissingReplyReferences(
        requestOptions: IRemoteRangeRequestOptions,
        models: IMessage[],
        onSuccess: () => void,
        onError: (error: unknown) => void
    ): void {
        const missingIds = this._collectMissingReplyTargetIds(requestOptions, models);

        if (!missingIds.length) {
            onSuccess();
            return;
        }

        const referenceResolver = this.context.getChatOptions().referenceResolver;

        if (typeof referenceResolver !== "function") {
            onError(new Error("The Chat referenceResolver option is required to render off-batch reply previews."));
            return;
        }

        let completed = false;

        referenceResolver({
            value: missingIds,
            success: (dataItems: IMessageReferenceData[]) => {
                if (completed) {
                    return;
                }

                completed = true;

                try {
                    const resolvedMessages = this._mapResolvedReferenceMessages(missingIds, dataItems);

                    this.context.cacheResolvedReferenceMessages(resolvedMessages);
                    onSuccess();
                } catch (error) {
                    onError(error);
                }
            },
            error: (error: unknown) => {
                if (completed) {
                    return;
                }

                completed = true;
                onError(error);
            }
        });
    }

    private _mapResolvedReferenceMessages(
        requestedIds: Array<string | number>,
        dataItems: IMessageReferenceData[]
    ): IMessageReferenceData[] {
        const resolvedById: Record<string, IMessageReferenceData> = {};

        (Array.isArray(dataItems) ? dataItems : []).forEach((item: IMessageReferenceData) => {
            this._validateResolvedReferenceMessage(item);

            const key = String(item.id);

            if (resolvedById[key]) {
                return;
            }

            resolvedById[key] = item;
        });

        return requestedIds.map((id: string | number) => {
            const resolvedMessage = resolvedById[String(id)];

            if (!resolvedMessage) {
                throw new Error("The Chat referenceResolver must resolve every requested id.");
            }

            return resolvedMessage;
        });
    }

    private _validateResolvedReferenceMessage(item: IMessageReferenceData): void {
        const hasId = item?.id !== undefined && item?.id !== null && String(item.id) !== "";
        const hasAuthorId = item?.authorId !== undefined && item?.authorId !== null && String(item.authorId) !== "";
        const hasText = typeof item?.text === "string" && item.text.length > 0;
        const hasFiles = !!item?.files && typeof item.files.length === "number" && item.files.length > 0;
        const isDeleted = item?.isDeleted === true;

        if (!hasId || !hasAuthorId || (!hasText && !hasFiles && !isDeleted)) {
            throw new Error("The Chat referenceResolver must return items with id, authorId, and enough data to render the reply preview.");
        }
    }

    private _normalizeReferenceResolverError(error: unknown): Error {
        if (error instanceof Error) {
            return error;
        }

        if (typeof error === "string" || typeof error === "number" || typeof error === "boolean" || typeof error === "bigint") {
            return new Error(`${error}`);
        }

        if (typeof error === "symbol") {
            return new Error(error.description || "The Chat referenceResolver failed.");
        }

        if (typeof error === "object" && error) {
            if ("message" in error && typeof error.message === "string") {
                return new Error(error.message);
            }

            try {
                return new Error(JSON.stringify(error));
            } catch {
                return new Error("The Chat referenceResolver failed.");
            }
        }

        return new Error("The Chat referenceResolver failed.");
    }

}
